import { useRef, useState } from "react"
import { uploadDocument, DocumentType, UploadWarningResponse } from "../../lib/api/documents"

interface DocumentUploaderProps {
  apiNumber: string
  onSuccess?: () => void
}

type UploadState = "idle" | "uploading" | "success" | "error" | "warning"

const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  w2:             "W-2 (Completion Report)",
  w15:            "W-15 (Well Log)",
  gau:            "GAU (Groundwater Advisory Unit)",
  schematic:      "Well Schematic",
  formation_tops: "Formation Tops",
  w3:             "W-3 (Plugging Report)",
  w3a:            "W-3A (Plugging Plan)",
  other:          "Other / Supporting",
}

const DOCUMENT_TYPES = Object.keys(DOCUMENT_TYPE_LABELS) as DocumentType[]

export default function DocumentUploader({ apiNumber, onSuccess }: DocumentUploaderProps) {
  const [file, setFile] = useState<File | null>(null)
  const [documentType, setDocumentType] = useState<DocumentType>("w2")
  const [uploadState, setUploadState] = useState<UploadState>("idle")
  const [successMessage, setSuccessMessage] = useState<string>("")
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [warningData, setWarningData] = useState<UploadWarningResponse | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null
    setFile(selected)
    // Reset status when a new file is chosen
    if (uploadState !== "idle") {
      setUploadState("idle")
      setSuccessMessage("")
      setErrorMessage("")
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) return

    setUploadState("uploading")
    setSuccessMessage("")
    setErrorMessage("")

    try {
      const response = await uploadDocument(apiNumber, file, documentType)
      if ("status" in response && response.status === "warning") {
        setUploadState("warning")
        setWarningData(response)
        return
      }
      setUploadState("success")
      setSuccessMessage(("message" in response ? response.message : undefined) || "Document uploaded successfully.")
      // Reset form
      setFile(null)
      setDocumentType("w2")
      if (inputRef.current) {
        inputRef.current.value = ""
      }
      onSuccess?.()
    } catch (err) {
      setUploadState("error")
      setErrorMessage(
        err instanceof Error ? err.message : "Upload failed. Please try again."
      )
    }
  }

  const handleConfirmUpload = async () => {
    if (!file) return
    setUploadState("uploading")
    setWarningData(null)
    try {
      const response = await uploadDocument(apiNumber, file, documentType, true)
      setUploadState("success")
      setSuccessMessage(
        ("message" in response ? response.message : undefined) || "Document uploaded successfully."
      )
      setFile(null)
      setDocumentType("w2")
      if (inputRef.current) inputRef.current.value = ""
      onSuccess?.()
    } catch (err) {
      setUploadState("error")
      setErrorMessage(
        err instanceof Error ? err.message : "Upload failed. Please try again."
      )
    }
  }

  const isUploading = uploadState === "uploading"
  const isWarning = uploadState === "warning"
  const canSubmit = file !== null && !isUploading && !isWarning

  return (
    <div className="space-y-5 max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* File input */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1.5">
            PDF File
          </label>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf"
            disabled={isUploading}
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-700 dark:text-gray-300
              file:mr-3 file:py-1.5 file:px-3
              file:rounded-md file:border-0
              file:text-xs file:font-medium
              file:bg-brand-50 file:text-brand-700
              dark:file:bg-brand-900/20 dark:file:text-brand-400
              hover:file:bg-brand-100 dark:hover:file:bg-brand-900/30
              file:cursor-pointer
              disabled:opacity-50 disabled:cursor-not-allowed
              cursor-pointer"
          />
          {file && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Selected: <span className="font-medium">{file.name}</span>{" "}
              ({(file.size / 1024).toFixed(0)} KB)
            </p>
          )}
        </div>

        {/* Document type select */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1.5">
            Document Type
          </label>
          <select
            value={documentType}
            disabled={isUploading}
            onChange={(e) => setDocumentType(e.target.value as DocumentType)}
            className="w-full rounded-lg border border-gray-300 dark:border-white/[0.1]
              bg-white dark:bg-white/[0.03]
              px-3 py-2 text-sm text-gray-800 dark:text-white/90
              focus:outline-none focus:ring-2 focus:ring-brand-500
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {DOCUMENT_TYPES.map((dt) => (
              <option key={dt} value={dt}>
                {DOCUMENT_TYPE_LABELS[dt]}
              </option>
            ))}
          </select>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full py-2.5 bg-brand-500 text-white text-sm font-medium rounded-lg
            hover:bg-brand-600
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors"
        >
          {isUploading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent" />
              Uploading...
            </span>
          ) : (
            "Upload Document"
          )}
        </button>
      </form>

      {/* Success message */}
      {uploadState === "success" && (
        <div className="flex items-start gap-3 rounded-xl border border-green-200 dark:border-green-800/40 bg-green-50 dark:bg-green-900/10 px-4 py-3">
          <svg
            className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-sm text-green-800 dark:text-green-300">{successMessage}</p>
        </div>
      )}

      {/* Error message */}
      {uploadState === "error" && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/10 px-4 py-3">
          <svg
            className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            />
          </svg>
          <p className="text-sm text-red-800 dark:text-red-300">{errorMessage}</p>
        </div>
      )}

      {/* Warning — API not found or mismatch */}
      {uploadState === "warning" && warningData && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-700/40 bg-amber-50 dark:bg-amber-900/10 px-4 py-4 space-y-3">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
            <p className="text-sm text-amber-800 dark:text-amber-300">
              {warningData.warning_code === "api_mismatch"
                ? `This document appears to be for well ${warningData.extracted_api}. You're uploading it to ${apiNumber}. Do you want to attach it anyway?`
                : `No API number was found in this document. Are you sure you want to associate it with well ${apiNumber}?`}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleConfirmUpload}
              className="flex-1 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Upload Anyway
            </button>
            <button
              onClick={() => { setUploadState("idle"); setWarningData(null) }}
              className="flex-1 py-2 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
